import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowLeft, ArrowRight, Send, CheckCheck, Smile, Paperclip, X, Image as ImageIcon, FileText, Trash2, Mic, Square } from 'lucide-react';
import { GroupMenuButton, GroupPanel, type GroupPanelView } from './GroupMenu';
import EmojiPicker from './EmojiPicker';
import MentionPopup from './MentionPopup';
import { toast } from 'sonner';
import { AI_BOT_ID, AI_BOT_NAME } from '@/lib/aiBot';
import { useCompanyId } from '@/hooks/useCompanyId';

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  senderName?: string;
  deleted?: boolean;
}

interface ChatViewProps {
  room: {id: string;type: string;name: string | null;otherUserName?: string;};
  onBack: () => void;
  onMarkRead?: () => void;
}

const ChatView = ({ room, onBack, onMarkRead }: ChatViewProps) => {
  const { user } = useAuth();
  const { t, dir, lang } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [currentRoomName, setCurrentRoomName] = useState(room.name);
  const [groupPanel, setGroupPanel] = useState<GroupPanelView>('none');
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [selectedMsgId, setSelectedMsgId] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const profileCache = useRef<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const companyId = useCompanyId();
  const isGroup = room.type !== 'direct';
  const roomTitle = room.otherUserName || currentRoomName || '';

  const getProfileName = async (userId: string) => {
    if (userId === AI_BOT_ID) return AI_BOT_NAME;
    if (profileCache.current[userId]) return profileCache.current[userId];
    const { data } = await supabase.from('profiles_public' as any).select('id, name').eq('id', userId).maybeSingle();
    const name = (data as any)?.name || 'مستخدم';
    profileCache.current[userId] = name;
    return name;
  };

  const cacheKey = `chat_msgs_${room.id}`;

  const saveToCache = (msgs: Message[]) => {
    try {
      const toCache = msgs.slice(-200); // keep last 200
      localStorage.setItem(cacheKey, JSON.stringify(toCache));
    } catch { /* quota exceeded */ }
  };

  const loadFromCache = (): Message[] => {
    try {
      const cached = localStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  };

  const fetchMessages = async () => {
    const { data } = await supabase.
    from('chat_messages').
    select('*').
    eq('room_id', room.id).
    order('created_at', { ascending: true });

    if (data) {
      const enriched = await Promise.all(
        data.map(async (msg) => ({
          ...msg,
          senderName: await getProfileName(msg.sender_id)
        }))
      );
      setMessages(enriched);
      saveToCache(enriched);
    }
  };

  useEffect(() => {
    const cached = loadFromCache();
    if (cached.length > 0) setMessages(cached);
    fetchMessages();

    const channel = supabase
      .channel(`room-${room.id}-${Date.now()}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `room_id=eq.${room.id}`
      }, async (payload) => {
        const newMessage = payload.new as Message;
        newMessage.senderName = await getProfileName(newMessage.sender_id);
        setMessages((prev) => {
          // Deduplicate by id
          if (prev.some(m => m.id === newMessage.id)) return prev;
          const updated = [...prev, newMessage];
          saveToCache(updated);
          return updated;
        });
        if (newMessage.sender_id !== user?.id) {
          onMarkRead?.();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [room.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const handler = () => onBack();
    window.addEventListener('left-group', handler);
    return () => window.removeEventListener('left-group', handler);
  }, [onBack]);

  const triggerAIReply = async (roomId: string) => {
    try {
      await supabase.functions.invoke('group-ai-reply', {
        body: { room_id: roomId },
      });
    } catch (e) {
      console.error('AI reply error:', e);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !user || sending) return;
    setSending(true);
    const content = newMsg.trim();
    setNewMsg('');
    setShowEmoji(false);

    await supabase.from('chat_messages').insert({
      room_id: room.id,
      sender_id: user.id,
      content
    });

    setSending(false);

    // Trigger AI reply if bot is enabled (fire and forget)
    triggerAIReply(room.id);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user) return;

    setUploading(true);
    setShowEmoji(false);

    for (const file of Array.from(files)) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('حجم الملف كبير جداً (الحد الأقصى 10 ميجا)');
        continue;
      }

      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error } = await supabase.storage.
      from('chat-media').
      upload(filePath, file);

      if (error) {
        toast.error('فشل رفع الملف');
        continue;
      }

      const { data: urlData } = await supabase.storage.
      from('chat-media').
      createSignedUrl(filePath, 3600);

      const isImage = file.type.startsWith('image/');
      const content = isImage ?
      `[صورة](${urlData?.signedUrl})` :
      `[ملف: ${file.name}](${urlData?.signedUrl})`;

      await supabase.from('chat_messages').insert({
        room_id: room.id,
        sender_id: user.id,
        content
      });
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteMessage = async (msgId: string) => {
    const { error } = await supabase.from('chat_messages').delete().eq('id', msgId);
    if (error) {
      toast.error(lang === 'ar' ? 'فشل حذف الرسالة' : 'Failed to delete message');
    } else {
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, deleted: true } : m));
    }
    setSelectedMsgId(null);
  };

  const handleLongPressStart = (msgId: string, isMine: boolean) => {
    if (!isMine) return;
    longPressTimer.current = setTimeout(() => setSelectedMsgId(msgId), 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };


  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingDuration(0);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size < 1000) return; // too short

        if (!user) return;
        const filePath = `${companyId || 'default'}/${user.id}/${Date.now()}-voice.webm`;
        const { error } = await supabase.storage.from('chat-media').upload(filePath, audioBlob);
        if (error) {
          toast.error(lang === 'ar' ? 'فشل رفع التسجيل الصوتي' : 'Failed to upload voice note');
          return;
        }
        const { data: urlData } = await supabase.storage.from('chat-media').createSignedUrl(filePath, 3600);
        await supabase.from('chat_messages').insert({
          room_id: room.id,
          sender_id: user.id,
          content: `[صوت](${urlData?.signedUrl})`
        });
      };

      mediaRecorder.start();
      setIsRecording(true);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch {
      toast.error(lang === 'ar' ? 'لا يمكن الوصول إلى الميكروفون' : 'Cannot access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingDuration(0);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current.stop();
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    setIsRecording(false);
    setRecordingDuration(0);
  };

  const formatRecordingTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDateSeparator = (dateStr: string) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    if (isSameDay(d, today)) return lang === 'ar' ? 'اليوم' : 'Today';
    if (isSameDay(d, yesterday)) return lang === 'ar' ? 'أمس' : 'Yesterday';
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getDateKey = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const sanitizeUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) return '#';
      return url;
    } catch { return '#'; }
  };

  const renderMessageContent = (content: string) => {
    // Check for voice notes: [صوت](url)
    const voiceMatch = content.match(/^\[صوت\]\((https?:\/\/.+)\)$/);
    if (voiceMatch) {
      const safeUrl = sanitizeUrl(voiceMatch[1]);
      return (
        <audio controls className="max-w-[240px]" preload="none">
          <source src={safeUrl} type="audio/webm" />
        </audio>);
    }

    // Check for image links: [صورة](url)
    const imageMatch = content.match(/^\[صورة\]\((https?:\/\/.+)\)$/);
    if (imageMatch) {
      const safeUrl = sanitizeUrl(imageMatch[1]);
      return (
        <a href={safeUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={safeUrl}
            alt="صورة"
            className="max-w-[240px] max-h-[240px] rounded-lg object-cover cursor-pointer"
            loading="lazy" />
        </a>);
    }

    // Check for file links: [ملف: name](url)
    const fileMatch = content.match(/^\[ملف: (.+?)\]\((https?:\/\/.+)\)$/);
    if (fileMatch) {
      const safeUrl = sanitizeUrl(fileMatch[2]);
      return (
        <a
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 bg-[#f0f0f0] rounded-lg hover:bg-[#e0e0e0] transition-colors">
          <FileText className="h-5 w-5 text-[#667781] shrink-0" />
          <span className="text-sm text-[#111b21] truncate">{fileMatch[1]}</span>
        </a>);
    }

    // Render text with @mention highlights
    const parts = content.split(/(@[\u0600-\u06FFa-zA-Z0-9_\s]+?)(?=\s|$)/g);
    return (
      <p className="text-sm text-[#111b21] whitespace-pre-wrap break-words">
        {parts.map((part, i) =>
          part.startsWith('@') ? (
            <span key={i} className="text-[#075E54] font-semibold">{part}</span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </p>
    );
  };

  const BackArrow = ArrowRight;

  return (
    <div className="flex flex-col h-full relative" dir="rtl">
      {/* WhatsApp Header */}
      <div className="flex items-center gap-3 px-3 py-2 bg-[#075E54] text-white shrink-0">
        <button onClick={onBack} className="p-1 hover:bg-white/10 rounded-full transition-colors">
          <BackArrow className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-base truncate">{roomTitle}</h3>
        </div>
        <GroupMenuButton onClick={() => setGroupPanel('menu')} />
      </div>

      {/* Chat background with WhatsApp pattern */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2 whatsapp-chat-bg"
        style={{ backgroundColor: '#ECE5DD' }}
        onClick={() => selectedMsgId && setSelectedMsgId(null)}>

        <div className="flex flex-col gap-1">
          {messages.map((msg, index) => {
            const isMine = msg.sender_id === user?.id;
            const currentDateKey = getDateKey(msg.created_at);
            const prevDateKey = index > 0 ? getDateKey(messages[index - 1].created_at) : null;
            const showDateSeparator = currentDateKey !== prevDateKey;

            return (
              <div key={msg.id}>
                {showDateSeparator &&
                <div className="flex justify-center my-3">
                    <span className="bg-[#e1f2fb] text-[#54656f] text-xs px-3 py-1 rounded-lg shadow-sm">
                      {formatDateSeparator(msg.created_at)}
                    </span>
                  </div>
                }
                <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1 group`}>
                  <div
                    className={`relative max-w-[80%] px-3 py-1.5 rounded-lg shadow-sm select-none ${
                    isMine ?
                    'bg-[#DCF8C6] rounded-tr-none rtl:rounded-tr-lg rtl:rounded-tl-none' :
                    'bg-white rounded-tl-none rtl:rounded-tl-lg rtl:rounded-tr-none'} ${
                    selectedMsgId === msg.id ? 'ring-2 ring-[#075E54]' : ''}`}
                    onTouchStart={() => handleLongPressStart(msg.id, isMine)}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressEnd}
                    onContextMenu={(e) => {
                      if (isMine) {
                        e.preventDefault();
                        setSelectedMsgId(selectedMsgId === msg.id ? null : msg.id);
                      }
                    }}>

                    {msg.deleted ?
                    <p className="text-sm text-[#8696a0] italic flex items-center gap-1">
                        <span>🚫</span>
                        {isMine ?
                      lang === 'ar' ? 'تم حذف هذه الرسالة' : 'You deleted this message' :
                      lang === 'ar' ? 'تم حذف هذه الرسالة' : 'This message was deleted'
                      }
                      </p> :

                    <>
                        {!isMine && room.type !== 'direct' &&
                      <p className={`text-xs font-medium mb-0.5 ${msg.sender_id === AI_BOT_ID ? 'text-[#34B7F1]' : 'text-[#075E54]'}`}>
                        {msg.sender_id === AI_BOT_ID ? '🤖 ' : ''}{msg.senderName}
                      </p>
                      }
                        {renderMessageContent(msg.content)}
                        <div className="flex items-center gap-1 justify-end mt-0.5">
                          <span className="text-[11px] text-[#667781] leading-none">{formatTime(msg.created_at)}</span>
                          {isMine && <CheckCheck className="h-3.5 w-3.5 text-[#53bdeb]" />}
                        </div>
                        {/* Delete popup */}
                        {selectedMsgId === msg.id && isMine &&
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10 flex items-center bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                            <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="flex items-center gap-1.5 px-3 py-2 text-red-600 hover:bg-red-50 transition-colors text-xs font-medium">

                              <Trash2 className="h-3.5 w-3.5" />
                              {lang === 'ar' ? 'حذف' : 'Delete'}
                            </button>
                          </div>
                      }
                      </>
                    }
                  </div>
                </div>
              </div>);

          })}
        </div>
      </div>

      {/* Input bar */}
      <div className="relative shrink-0">
        {mentionQuery !== null && (
          <MentionPopup
            roomId={room.id}
            query={mentionQuery}
            onSelect={(member) => {
              setNewMsg((prev) => {
                const atIdx = prev.lastIndexOf('@');
                return prev.slice(0, atIdx) + `@${member.name} `;
              });
              setMentionQuery(null);
            }}
            onClose={() => setMentionQuery(null)}
          />
        )}
        {showEmoji &&
        <EmojiPicker
          onSelect={(emoji) => setNewMsg((prev) => prev + emoji)}
          onClose={() => setShowEmoji(false)} />

        }
        {isRecording ?
        <div className="flex items-center gap-2 px-2 py-2 bg-[#f0f0f0] w-full">
            <button
            onClick={cancelRecording}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#e0e0e0] transition-colors shrink-0">

              <Trash2 className="h-5 w-5 text-red-500" />
            </button>
            <div className="flex-1 flex items-center justify-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-medium text-[#111b21]">{formatRecordingTime(recordingDuration)}</span>
            </div>
            <button
            onClick={stopRecording}
            className="w-10 h-10 rounded-full text-white flex items-center justify-center transition-colors shrink-0 bg-secondary-foreground">

              <Send className="h-5 w-5 rtl:rotate-180" />
            </button>
          </div> :

        <div className="flex items-center gap-1.5 px-2 py-2 bg-[#f0f0f0]">
            <button
            onClick={() => setShowEmoji(!showEmoji)}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#e0e0e0] transition-colors shrink-0">

              {showEmoji ? <X className="h-5 w-5 text-[#667781]" /> : <Smile className="h-5 w-5 text-[#667781]" />}
            </button>
            <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#e0e0e0] transition-colors shrink-0 disabled:opacity-50">

              <Paperclip className="h-5 w-5 text-[#667781]" />
            </button>
            <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleFileUpload}
            className="hidden" />

            <input
            type="text"
            value={newMsg}
            onChange={(e) => {
              const val = e.target.value;
              setNewMsg(val);
              // Detect @mention
              const cursorPos = e.target.selectionStart || val.length;
              const textBeforeCursor = val.slice(0, cursorPos);
              const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);
              if (atMatch) {
                setMentionQuery(atMatch[1]);
              } else {
                setMentionQuery(null);
              }
            }}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            onFocus={() => setShowEmoji(false)}
            placeholder={t('typeMessage')}
            className="flex-1 px-4 py-2 rounded-full bg-white text-sm outline-none text-[#111b21] placeholder:text-[#667781]" />

            {newMsg.trim() ?
          <button
            onClick={handleSend}
            disabled={sending}
            className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#128C7E] transition-colors disabled:opacity-50 shrink-0">

                {uploading ?
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

            <Send className="h-5 w-5 rtl:rotate-180" />
            }
              </button> :

          <button
            onClick={startRecording}
            disabled={uploading}
            className="w-10 h-10 rounded-full text-white flex items-center justify-center transition-colors disabled:opacity-50 shrink-0 bg-black">

                {uploading ?
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

            <Mic className="h-5 w-5" />
            }
              </button>
          }
          </div>
        }
      </div>

      {/* Group panel overlay */}
      <GroupPanel
        room={{ ...room, name: currentRoomName ?? null }}
        view={groupPanel}
        onClose={() => setGroupPanel('none')}
        onViewChange={setGroupPanel}
        onNameUpdated={(newName) => setCurrentRoomName(newName)} />

    </div>);

};

export default ChatView;